import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ShieldCheck, X } from "lucide-react";

type Props = {
  userId: number | null | undefined;
  totpEnabled: boolean | null | undefined;
};

const STORAGE_KEY_PREFIX = "padpulse_2fa_nudge_dismissed_";

function storageKey(userId: number) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

export function TwoFactorNudgeBanner({ userId, totpEnabled }: Props) {
  const [dismissed, setDismissed] = useState<boolean>(true);

  useEffect(() => {
    if (!userId) {
      setDismissed(true);
      return;
    }
    try {
      setDismissed(localStorage.getItem(storageKey(userId)) === "1");
    } catch {
      setDismissed(false);
    }
  }, [userId]);

  if (!userId || totpEnabled || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey(userId), "1");
    } catch {
      // ignore storage errors; still dismiss in-session
    }
    setDismissed(true);
  };

  return (
    <div
      className="border border-amber-200 bg-amber-50 text-amber-900 rounded-lg"
      data-testid="banner-2fa-nudge"
    >
      <div className="px-4 py-3 flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-5 w-5 shrink-0 text-amber-700" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold">Protect your account with two-factor authentication.</span>{" "}
          <span className="text-amber-800">
            Add a 6-digit code from your authenticator app at sign-in to make account takeover much harder.
          </span>
        </div>
        <Link
          href="/app/profile?tab=security"
          className="text-sm font-medium px-3 py-1.5 rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
          data-testid="link-2fa-nudge-enable"
        >
          Turn on 2FA
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-amber-100"
          aria-label="Dismiss two-factor authentication reminder"
          data-testid="button-2fa-nudge-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
